import { useState, useEffect, useRef } from 'react';
import { useCommandPaletteContext } from '../CommandPaletteContext';

/**
 * Returns a debounced copy of the current palette search value.
 * Reads `searchValue` from context so callers don't need to do so themselves.
 *
 * @param delay - debounce delay in milliseconds (default 300)
 */
export const useDebouncedSearch = (delay = 300): string => {
  const { searchValue } = useCommandPaletteContext();
  const [debouncedValue, setDebouncedValue] = useState(searchValue);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (delay === 0) {
      setDebouncedValue(searchValue);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedValue(searchValue), delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchValue, delay]);

  return debouncedValue;
};
